import { useAsyncAction } from "nate-react-api-helpers";
import { useCallback, useState } from "react";
import { useSnackbar } from "./Snackbar";
import { useSyncedRef } from "./SyncedRef";

// return SnackCancel from the callback to end the action quietly — the loading
// snack is hidden and no success message shows (e.g. the user backed out of a
// confirmation mid-action).
export const SnackCancel = Symbol("snack-cancel");

const snackMessage = Symbol("snack-message");

type SnackMessage = {
    [snackMessage]: true;
    text: string;
    onUndo?: () => void;
}

// snackText replaces the success message with one the result decides
export function snackText(text: string): SnackMessage {
    return { [snackMessage]: true, text: text };
}

// snackUndo shows the success message with an Undo button
export function snackUndo(text: string, onUndo: () => void): SnackMessage {
    return { [snackMessage]: true, text: text, onUndo: onUndo };
}

export function useSnackAction<TInput>(
    callback: (input: TInput) => Promise<any>,
    // loading is the text shown while the action runs
    props: { success: string; loading?: string }
) {
    const callbackRef = useSyncedRef(callback);
    const successRef = useSyncedRef(props.success);
    const loadingRef = useSyncedRef(props.loading);
    const snack = useSnackbar();
    const [loading, setLoading] = useState(false);

    const action = useAsyncAction(async (input: TInput) => {
        try {
            setLoading(true);
            snack.loading(loadingRef.current);

            const result = await callbackRef.current(input);
            if (result === SnackCancel) snack.hide();
            else if (result && result[snackMessage] && result.onUndo) snack.undo(result.text, result.onUndo);
            else if (result && result[snackMessage]) snack.success(result.text);
            else snack.success(successRef.current);
        } catch (e: any) {
            console.error(e);
            snack.error(e.message);
        }

        setLoading(false);
    }, []);

    const actionCallbackRef = useSyncedRef(action.callback);
    const cb = useCallback((input: TInput) => actionCallbackRef.current(input), [actionCallbackRef]);

    return {
        callback: cb,
        loading,
    };
}
